<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import InitialsAvatar from '@uiKit/components/InitialsAvatar.vue'
import AmountField from './AmountField.vue'
import MoneyModalHeader from './MoneyModalHeader.vue'
import SelectBankPopover from './SelectBankPopover.vue'
import WithdrawSummary from './WithdrawSummary.vue'
import { DEFAULT_WITHDRAWAL_AMOUNT } from '../constants/fiatWalletConstants'
import type { BankAccount } from '@modules/account'

interface Props {
  visible: boolean
  currency?: string
  /** Spendable balance as a number — drives the cap and the remaining figure. */
  balanceAmount?: number
  accounts?: BankAccount[]
  defaultBankAccount?: BankAccount | null
  submitting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currency: 'AED',
  balanceAmount: 0,
  accounts: () => [],
  defaultBankAccount: null,
  submitting: false,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', payload: { amount: number; bankAccountId: string }): void
  (e: 'add-bank'): void
}>()

function initialAmount(balance: number): number {
  if (balance <= 0) return 0
  return Math.min(DEFAULT_WITHDRAWAL_AMOUNT, balance)
}

const amount = ref(initialAmount(props.balanceAmount))
const selectedBank = ref<BankAccount | null>(props.defaultBankAccount)
const bankPickerRef = ref<InstanceType<typeof SelectBankPopover> | null>(null)
// Zero-height marker on the bank card's right edge: lines the picker up with it.
const bankPickerAnchor = ref<{ $el: HTMLElement } | null>(null)
const error = ref('')

function formatMoney(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const remaining = computed(() => Math.max(0, props.balanceAmount - amount.value))
const hasBank = computed(() => selectedBank.value !== null)

const bankName = computed(() => selectedBank.value?.bankName ?? 'No bank on file')
const VISIBLE_DIGITS = 4

const bankLine = computed(() => {
  const bank = selectedBank.value
  if (!bank) return 'Add one to receive your funds'
  // Figma 32590:77489: spaced asterisks, then the last four digits ("* * * * 9934").
  const masked = `* * * * ${bank.accountNumber.slice(-VISIBLE_DIGITS)}`
  return bank.isPrimary ? `${masked} ·` : masked
})
const isPrimaryBank = computed(() => selectedBank.value?.isPrimary === true)

watch([amount, selectedBank], () => {
  if (error.value) error.value = ''
})

watch(
  () => props.defaultBankAccount,
  (bank) => {
    if (!selectedBank.value) selectedBank.value = bank
  },
)

// Re-seed on every open: balance and bank list may have moved since last time.
watch(
  () => props.visible,
  (open) => {
    if (!open) {
      error.value = ''
      bankPickerRef.value?.hide()
      return
    }
    amount.value = initialAmount(props.balanceAmount)
    selectedBank.value = props.defaultBankAccount
    error.value = ''
  },
)

const close = () => emit('update:visible', false)

const onDialogUpdate = (value: boolean) => {
  if (!value && !props.submitting) close()
}

const onBankAction = (event: Event) => {
  if (hasBank.value) {
    bankPickerRef.value?.toggle(event, bankPickerAnchor.value?.$el)
    return
  }
  emit('add-bank')
}

// Validated live so the over-balance case answers as the investor types, which
// is what the design shows: red field, message under it, Confirm disabled.
const amountError = computed(() => {
  if (amount.value > props.balanceAmount) {
    return 'The amount you want to withdraw exceeds the available amount'
  }
  return ''
})

const bankError = computed(() => {
  const bank = selectedBank.value
  if (!bank) return 'Add a bank account to receive the funds.'
  if (bank.accountStatus !== 'verified') {
    return 'Pick a verified bank account — this one is still being checked.'
  }
  return ''
})

const canSubmit = computed(
  () => amount.value > 0 && !amountError.value && !bankError.value && !props.submitting,
)

const onConfirm = () => {
  const bank = selectedBank.value
  if (!canSubmit.value || !bank) {
    // The amount problem is already shown under the field; surface the bank one.
    error.value = bankError.value
    return
  }
  error.value = ''
  emit('confirm', { amount: amount.value, bankAccountId: bank.id })
}
</script>

<template>
  <Dialog
    :model-value="visible"
    width="660px"
    :padded="false"
    :show-close-icon="false"
    :dismissable="!submitting"
    @update:model-value="onDialogUpdate"
  >
    <Box direction="col" class="wfm">
      <MoneyModalHeader title="Withdraw Funds" :disabled="submitting" @close="close" />

      <Box class="wfm__divider" />

      <Box direction="col" gap="6" class="wfm__body">
        <Box direction="col" gap="1">
          <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
            Available to withdraw
          </Text>
          <Text type="display" weight="semibold" is="p" class="wfm__balance">
            <CurrencyText :text="`${currency} ${formatMoney(balanceAmount)}`" />
          </Text>
        </Box>

        <Box direction="col" gap="3">
          <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
            Withdrawal amount
          </Text>
          <!-- `clamp="false"`: let an over-balance amount be typed so the
               inline error can answer it, rather than silently rewriting it. -->
          <AmountField
            v-model="amount"
            :currency="currency"
            :max="balanceAmount"
            :clamp="false"
            stepper
            :invalid="!!amountError"
            :error="amountError"
          />
        </Box>

        <Box direction="col" gap="3">
          <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
            Transfer to
          </Text>
          <Box align="center" gap="4" class="wfm__bank">
            <Box ref="bankPickerAnchor" class="wfm__bank-anchor" aria-hidden="true" />
            <!-- The bundled bank mark when there is one; otherwise "EM". -->
            <InitialsAvatar
              v-if="hasBank"
              :name="bankName"
              :src="selectedBank?.logoSrc"
              :letters="2"
              :size="42"
              fit="contain"
              radius="12px"
            />
            <Box v-else flex align="center" justify="center" class="wfm__bank-icon">
              <PiIcon name="building-columns" class="wfm__bank-icon-glyph" />
            </Box>
            <Box direction="col" gap="1" grow class="wfm__bank-mid">
              <Text type="heading" weight="medium" is="span" truncate>{{ bankName }}</Text>
              <Box align="center" gap="3px" class="wfm__bank-line">
                <Text type="body" color="muted" is="span" truncate>{{ bankLine }}</Text>
                <Box v-if="isPrimaryBank" align="center" class="wfm__bank-badge">
                  <Text type="body" weight="semibold" color="var(--p-surface-500)" is="span">
                    PRIMARY
                  </Text>
                </Box>
              </Box>
            </Box>
            <AppButton
              :label="hasBank ? 'Change' : 'Add bank'"
              color="secondary"
              variant="outline"
              size="small"
              class="wfm__bank-action"
              @click="onBankAction"
            />
          </Box>
        </Box>

        <WithdrawSummary
          :currency="currency"
          :amount="formatMoney(amount)"
          :remaining="formatMoney(remaining)"
        />

        <Box v-if="error" align="center" gap="2" class="wfm__error">
          <PiIcon name="exclamation-circle" class="wfm__error-icon" />
          <Text type="body" color="danger" is="span"><CurrencyText :text="error" /></Text>
        </Box>
      </Box>

      <Box class="wfm__divider" />

      <Box class="wfm__footer">
        <AppButton
          label="Confirm Withdrawal"
          size="large"
          block
          :loading="submitting"
          :disabled="!canSubmit"
          @click="onConfirm"
        />
      </Box>
    </Box>

    <SelectBankPopover
      ref="bankPickerRef"
      :accounts="accounts"
      :selected-id="selectedBank?.id ?? ''"
      @select="selectedBank = $event"
    />
  </Dialog>
</template>

<style scoped>
.wfm {
  max-height: calc(100vh - 6rem);
}

.wfm__divider {
  flex-shrink: 0;
  height: 1px;
  background: var(--p-content-border-color);
}

.wfm__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem 1.75rem;
  box-sizing: border-box;
}

.wfm__balance {
  font-variant-numeric: tabular-nums;
}

.wfm__bank {
  position: relative;
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  padding: 1rem 1.125rem;
  box-sizing: border-box;
}

/* Same width as the bank picker, flush with the card's right edge. */
.wfm__bank-anchor {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 304px;
  max-width: 100%;
  height: 0;
  pointer-events: none;
}

.wfm__bank-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: var(--p-surface-100);
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.wfm__bank-icon-glyph {
  font-size: 18px;
}

.wfm__bank-mid {
  min-width: 0;
}

.wfm__bank-line {
  min-width: 0;
}

.wfm__bank-badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--p-surface-100);
}

.wfm__bank-action {
  flex-shrink: 0;
}

.wfm__error-icon {
  font-size: 14px;
  color: var(--p-danger-600);
  flex-shrink: 0;
}

.wfm__footer {
  flex-shrink: 0;
  padding: 1.25rem 1.75rem 1.5rem;
}

@media (max-width: 992px) {
  .wfm__body {
    padding: 1.25rem 1rem;
  }

  .wfm__footer {
    padding: 1rem;
  }

  .wfm__bank {
    flex-wrap: wrap;
    row-gap: 0.75rem;
  }

  .wfm__bank-action {
    width: 100%;
  }
}
</style>
